// Tela de importação de OS via .csv (item 1 do /design de 2026-09). Mesma
// estrutura do import de preços (`_precos-io.ts`): modelo para baixar, prévia
// com erro por linha. Um comando `lote.lancarEmMassa` (transição atômica); o
// resumo (importadas × puladas) sai do diff do Snapshot.

import { formatData, hojeISO } from "../../domain/datas.ts";
import { criarAcaoComando } from "./_acao.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, botao } from "../ui.ts";
import { baixarTexto, nomeArquivo } from "./_folha-canvas.ts";
import {
  analisarImportacaoOS,
  contarOSValidas,
  entradasDaAnalise,
  modeloImportacaoOSCsv,
  type AnaliseImportacaoOS,
  type GrupoOS,
  type GrupoOSInvalido,
  type GrupoOSValido,
} from "./_os-import-parse.ts";

interface ResultadoAplicacao {
  importadas: number;
  falhas: { os: string; motivo: string }[];
}

export function abrirImportarOS(store: Store): void {
  openModal("Importar OS", (fechar) => {
    const baixarModelo = botao(
      "link",
      {},
      "Baixar Modelo (.csv)",
    );
    baixarModelo.addEventListener("click", () =>
      baixarTexto(
        nomeArquivo("modelo-importar-os", null, hojeISO(), "csv"),
        modeloImportacaoOSCsv(store.get()),
      ),
    );

    const arquivo = el("input", {
      type: "file",
      accept: ".csv,.txt,.tsv",
    }) as HTMLInputElement;
    const cola = el("textarea", {
      rows: "6",
      placeholder:
        "Cole o arquivo. 1ª linha = cabeçalho.\n" +
        "cliente;os;origem;data;hora;sacos;Lençol Casal;Fronha;Toalha Banho\n" +
        "HCP;1044;rotina;04/09/2026;;3;40;36;\n" +
        "PSV;2201;;04/09/2026;;;;;20",
    }) as HTMLTextAreaElement;

    const previa = el("div", { class: "import-previa" });
    const erroEl = el("p", { class: "form-erro" });
    let analise: AnaliseImportacaoOS = { grupos: [], colunasIgnoradas: [] };

    const aplicar = botao(
      "primario",
      {},
      "Importar",
    ) as HTMLButtonElement;
    aplicar.disabled = true;

    const analisar = (): void => {
      erroEl.textContent = "";
      analise = analisarImportacaoOS(cola.value, store.get());
      desenharPreviaOS(previa, analise);
      aplicar.disabled = contarOSValidas(analise) === 0;
    };

    const corpo = el("div", { class: "form-modal" });

    const mostrarResumo = (a: AnaliseImportacaoOS, r: ResultadoAplicacao): void => {
      clear(corpo);
      corpo.append(
        el("p", { class: "aviso aviso-sucesso" }, `${r.importadas} OS importada(s).`),
      );

      const invalidas = a.grupos.filter((g): g is GrupoOSInvalido => !g.valido);
      const naoImportadas = [
        ...invalidas.map((g) => ({ os: g.os, motivo: g.motivo })),
        ...r.falhas,
      ];
      if (naoImportadas.length > 0) {
        corpo.append(
          el(
            "p",
            { class: "muted" },
            `${naoImportadas.length} OS não importada(s):`,
          ),
          el(
            "table",
            { class: "mini" },
            el(
              "tbody",
              {},
              ...naoImportadas.map((f) =>
                el(
                  "tr",
                  { class: "import-erro" },
                  el("td", {}, f.os || "(sem OS)"),
                  el("td", { class: "muted" }, f.motivo),
                ),
              ),
            ),
          ),
        );
      }

      if (a.colunasIgnoradas.length > 0) {
        corpo.append(
          el(
            "p",
            { class: "muted" },
            `Coluna(s) ignorada(s): ${a.colunasIgnoradas
              .map((c) => `"${c.rotulo}" (${c.motivo})`)
              .join(", ")}.`,
          ),
        );
      }

      const itensIgnorados = a.grupos
        .filter((g): g is GrupoOSValido => g.valido)
        .reduce((n, g) => n + g.itensIgnorados.length, 0);
      if (itensIgnorados > 0) {
        corpo.append(
          el(
            "p",
            { class: "muted" },
            `${itensIgnorados} quantidade(s) ignorada(s) dentro de OS importadas ` +
              "(valor inválido na célula).",
          ),
        );
      }

      const ok = botao("primario", {}, "Fechar");
      ok.addEventListener("click", fechar);
      corpo.append(el("div", { class: "modal-actions" }, ok));
    };

    arquivo.addEventListener("change", () => {
      const f = arquivo.files?.[0];
      if (!f) return;
      const fr = new FileReader();
      fr.onload = () => {
        cola.value = String(fr.result ?? "");
        analisar();
      };
      fr.onerror = () => {
        erroEl.textContent = "Não foi possível ler o arquivo.";
      };
      fr.readAsText(f, "utf-8");
    });
    cola.addEventListener("input", analisar);

    aplicar.addEventListener("click", () => {
      const validos = analise.grupos.filter((g): g is GrupoOSValido => g.valido);
      if (validos.length === 0) return;
      const a = analise;
      aplicar.disabled = true;

      const osAntes = new Set(store.get().lotes.map((l) => l.os));
      const entradas = entradasDaAnalise(a);

      void criarAcaoComando(store, erroEl)(
        { agregado: "lote", cmd: { tipo: "lancarEmMassa", entradas } },
        null, // o resumo abaixo já informa o resultado
        () => {
          // resumo pelo diff: OS marcada que não entrou já existia no sistema
          const osDepois = new Set(store.get().lotes.map((l) => l.os));
          const resultado: ResultadoAplicacao = { importadas: 0, falhas: [] };
          for (const g of validos) {
            if (osDepois.has(g.os) && !osAntes.has(g.os)) {
              resultado.importadas += 1;
            } else {
              resultado.falhas.push({
                os: String(g.os),
                motivo: osAntes.has(g.os)
                  ? "OS já lançada."
                  : "Não foi possível importar.",
              });
            }
          }
          mostrarResumo(a, resultado);
        },
      ).finally(() => {
        if (erroEl.textContent) aplicar.disabled = false;
      });
    });

    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);

    corpo.append(
      el(
        "p",
        { class: "muted import-dica" },
        "Uma linha por OS. Os tipos de peça vão nas colunas depois de " +
          "cliente/os/origem/data/hora/sacos; a célula é a quantidade recebida " +
          "(inclua só os tipos que precisar). OS que já existe, ou que se repete " +
          "no arquivo, é ignorada — o resto entra normalmente. ",
        baixarModelo,
      ),
      el("label", {}, "Arquivo (.csv / .txt)", arquivo),
      el("label", {}, "Ou Cole", cola),
      previa,
      erroEl,
      el("div", { class: "modal-actions" }, cancelar, aplicar),
    );
    return corpo;
  });
}

function desenharPreviaOS(root: HTMLElement, a: AnaliseImportacaoOS): void {
  clear(root);
  if (a.erroArquivo) {
    root.append(el("p", { class: "form-erro" }, a.erroArquivo));
    return;
  }
  if (a.grupos.length === 0) return;

  root.append(
    el(
      "p",
      { class: "muted" },
      `${contarOSValidas(a)} de ${a.grupos.length} OS prontas para importar.`,
    ),
  );
  if (a.colunasIgnoradas.length > 0) {
    root.append(
      el(
        "p",
        { class: "muted" },
        `Coluna(s) ignorada(s): ${a.colunasIgnoradas
          .map((c) => `"${c.rotulo}" (${c.motivo})`)
          .join(", ")}.`,
      ),
    );
  }

  const linha = (g: GrupoOS): HTMLElement => {
    if (!g.valido) {
      return el(
        "tr",
        { class: "import-erro" },
        el("td", {}, g.os || "—"),
        el("td", { colspan: "5", class: "muted" }, g.motivo),
      );
    }
    const totalPecas = g.itens.reduce((n, i) => n + i.quantidade, 0);
    const obs =
      g.itensIgnorados.length > 0
        ? `${g.itensIgnorados.length} célula(s) ignorada(s)`
        : "";
    return el(
      "tr",
      {},
      el("td", {}, el("b", {}, String(g.os))),
      el("td", {}, g.clienteNome),
      el("td", { class: "muted" }, g.origemNome),
      el(
        "td",
        { class: "muted" },
        `${formatData(g.coletaData)} ${g.coletaHora}`,
      ),
      el("td", { class: "num" }, `${g.itens.length} tipo(s), ${totalPecas} peça(s)`),
      el("td", { class: "muted" }, obs),
    );
  };

  root.append(
    el(
      "div",
      { class: "table-wrap" },
      el(
        "table",
        { class: "mini" },
        el(
          "thead",
          {},
          el(
            "tr",
            {},
            el("th", {}, "OS"),
            el("th", {}, "Hotel"),
            el("th", {}, "Origem"),
            el("th", {}, "Coleta"),
            el("th", {}, "Itens"),
            el("th", {}, "Observação"),
          ),
        ),
        el("tbody", {}, ...a.grupos.map(linha)),
      ),
    ),
  );
}
